/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment, useRef, useEffect} from 'react'
import PropTypes from 'prop-types'

// Project Components
import {ListMenuContent} from './list-menu-content'
import {ListMenuTrigger} from './list-menu-trigger'

// Components
import {
    Popover,
    PopoverContent,
    PopoverBody,

    // Hooks
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

const CLOSE_ANIMATION_MS = 200

const ListMenuPopover = ({
    contentComponent,
    item,
    name,
    itemsKey,
    maxColumns,
    categories,
    isActive,
    isOpen,
    isKeyboardOpen,
    isClosing,
    skipOpenAnimation,
    onClose,
    onMenuMouseEnter,
    onMenuMouseLeave,
    onKeyboardToggle,
    onEscapeKey,
    onFocusLeave,
    toggleButtonRef,
    /* Optional CMS CTA overrides for the trigger (CategoryAddReplace mega-link without a CMS link) */
    triggerDisplayName,
    triggerAriaLabel,
    triggerLinkProps,
    triggerHoverProps,
    triggerSx,
    customTrigger
}) => {
    const popoverContentRef = useRef()
    const ContentComponent = contentComponent || ListMenuContent
    const baseStyle = useMultiStyleConfig('ListMenu', {})
    const popoverId = `nav-submenu-${item?.id}`

    // When opened via keyboard, shift focus to the first focusable link/button inside.
    // A small timeout (50ms) is needed to reliably beat Chakra UI's internal Popover
    // focus-management which runs in its own effects and would otherwise focus the
    // PopoverContent wrapper div instead.
    useEffect(() => {
        if (!isOpen || !isKeyboardOpen || !popoverContentRef.current) return
        const id = setTimeout(() => {
            const firstFocusable = popoverContentRef.current?.querySelector(
                'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
            firstFocusable?.focus()
        }, 50)
        return () => clearTimeout(id)
    }, [isOpen, isKeyboardOpen])

    return (
        <Fragment>
            <Popover
                placement="bottom-start"
                {...baseStyle.popoverContainer}
                isLazy
                isOpen={isOpen}
                variant="fullWidth"
                autoFocus={false}
                returnFocusOnClose={false}
                closeOnBlur={false}
            >
                <Fragment>
                    <ListMenuTrigger
                        item={item}
                        name={name}
                        isOpen={isOpen}
                        isActive={isActive}
                        onKeyboardToggle={onKeyboardToggle}
                        toggleButtonRef={toggleButtonRef}
                        popoverId={popoverId}
                        displayName={triggerDisplayName}
                        ariaLabel={triggerAriaLabel}
                        extraLinkProps={triggerLinkProps}
                        extraHoverProps={triggerHoverProps}
                        triggerSx={triggerSx}
                        customTrigger={customTrigger}
                    />
                    {isOpen && (
                        <PopoverContent
                            ref={popoverContentRef}
                            id={popoverId}
                            role="region"
                            aria-label={name}
                            data-testid="popover-menu"
                            {...baseStyle.popoverContent}
                            onMouseEnter={onMenuMouseEnter}
                            onMouseLeave={onMenuMouseLeave}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    onEscapeKey?.()
                                }
                            }}
                            onBlur={(e) => {
                                // Close when focus leaves the popover entirely (Tab-out)
                                if (
                                    e.relatedTarget === null ||
                                    !e.currentTarget.contains(e.relatedTarget)
                                ) {
                                    onFocusLeave?.()
                                }
                            }}
                            sx={{
                                transformOrigin: 'top center',
                                '@keyframes megaMenuOpen': {
                                    from: {opacity: 0, transform: 'translateY(-4px)'},
                                    to: {opacity: 1, transform: 'translateY(0)'}
                                },
                                '@keyframes megaMenuClose': {
                                    from: {opacity: 1, transform: 'scaleY(1)'},
                                    to: {opacity: 0, transform: 'scaleY(0)'}
                                },
                                animation: isClosing
                                    ? `megaMenuClose ${CLOSE_ANIMATION_MS}ms ease forwards`
                                    : skipOpenAnimation
                                    ? 'none'
                                    : 'megaMenuOpen 150ms ease'
                            }}
                        >
                            <PopoverBody {...baseStyle.popoverBody}>
                                <ContentComponent
                                    item={item}
                                    itemsKey={itemsKey}
                                    onClose={onClose}
                                    maxColumns={maxColumns}
                                    categories={categories}
                                />
                            </PopoverBody>
                        </PopoverContent>
                    )}
                </Fragment>
            </Popover>
        </Fragment>
    )
}

ListMenuPopover.propTypes = {
    contentComponent: PropTypes.elementType,
    item: PropTypes.object,
    name: PropTypes.string,
    maxColumns: PropTypes.number,
    itemsKey: PropTypes.string,
    categories: PropTypes.object,
    isActive: PropTypes.bool,
    isOpen: PropTypes.bool,
    isKeyboardOpen: PropTypes.bool,
    isClosing: PropTypes.bool,
    skipOpenAnimation: PropTypes.bool,
    onClose: PropTypes.func,
    onMenuMouseEnter: PropTypes.func,
    onMenuMouseLeave: PropTypes.func,
    onKeyboardToggle: PropTypes.func,
    onEscapeKey: PropTypes.func,
    onFocusLeave: PropTypes.func,
    toggleButtonRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    triggerDisplayName: PropTypes.string,
    triggerAriaLabel: PropTypes.string,
    triggerLinkProps: PropTypes.object,
    triggerHoverProps: PropTypes.object,
    triggerSx: PropTypes.object,
    customTrigger: PropTypes.node
}

export {ListMenuPopover}
